/** Shared inline validation UI — same “input box” look app-wide (Login / modals / profiles). */

const BOX_ROUND = "rounded-[8px]";

const FOCUS_OK =
  "focus:border-[#2b3896] focus:ring-2 focus:ring-[#4551af]/35 focus:outline-none";
const FOCUS_ERR =
  "focus:border-red-600 focus:ring-2 focus:ring-red-200 focus:outline-none";

const SHELL_FOCUS_OK =
  "focus-within:border-[#2b3896] focus-within:ring-2 focus-within:ring-[#4551af]/35 focus-within:outline-none";
const SHELL_FOCUS_ERR =
  "focus-within:border-red-600 focus-within:ring-2 focus-within:ring-red-200 focus-within:outline-none";

export function emailLooksValid(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

export function normalizePhone10(value) {
  return String(value || "").replace(/\D/g, "").slice(0, 10);
}

export function isPhone10Digits(value) {
  return /^\d{10}$/.test(normalizePhone10(value));
}

/** Live input: allow + prefix, digits, spaces, dashes (not truncated to 10). */
export function normalizePhoneInput(value) {
  const raw = String(value || "");
  let out = "";
  for (let i = 0; i < raw.length; i += 1) {
    const c = raw[i];
    if (c >= "0" && c <= "9") out += c;
    else if (c === "+" && out === "") out += "+";
    else if ((c === " " || c === "-" || c === "(" || c === ")") && out.length > 0) out += c;
  }
  return out.slice(0, 24);
}

/** Storage format: 10-digit local or +country code (max 15 digits). */
export function normalizePhone(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const hasPlus = raw.startsWith("+");
  const digits = raw.replace(/\D/g, "");
  if (!digits) return "";
  if (hasPlus) return `+${digits.slice(0, 15)}`;
  return digits.slice(0, 15);
}

/** Valid: 10-digit local or international 8–15 digits (optional +). */
export function isValidPhone(value) {
  const normalized = normalizePhone(value);
  if (!normalized) return false;
  const digits = normalized.replace(/\D/g, "");
  if (digits.length === 10) return true;
  if (normalized.startsWith("+")) {
    return digits.length >= 8 && digits.length <= 15;
  }
  return digits.length >= 10 && digits.length <= 15;
}

/** Label above inputs in modals (school form style) */
export const FIELD_LABEL_CLASS =
  "mb-1 block text-sm font-semibold text-slate-800";

/** Red helper under inputs */
export const FIELD_ERROR_CLASS = "mt-1 text-xs font-medium text-red-600";

/** Border, radius, focus — shared by standalone inputs and selects. */
function fieldControlSurface(hasError) {
  return hasError
    ? `border-2 border-red-500 bg-red-50/50 ${FOCUS_ERR}`
    : `border-2 border-slate-200 bg-white shadow-sm ${FOCUS_OK}`;
}

/** Full-width text / email / password input (single element). */
export function fieldInputClassName(hasError) {
  return `w-full min-w-0 min-h-11 ${BOX_ROUND} px-4 py-2.5 text-sm text-slate-700 outline-none transition ${fieldControlSurface(hasError)}`;
}

/**
 * Native `<select>` — same box as inputs.
 * @param {boolean} hasError
 * @param {{ compact?: boolean }} [opts] — compact: inline width (e.g. pager) instead of full width
 */
export function fieldSelectClassName(hasError, opts = {}) {
  const width = opts.compact
    ? "w-auto min-w-[5.5rem]"
    : "w-full min-w-0";
  return `${width} min-h-11 ${BOX_ROUND} px-4 py-2.5 text-sm text-slate-700 outline-none transition cursor-pointer ${fieldControlSurface(hasError)}`;
}

/** Same as modal input; kept for call sites that expect a “large” profile field. */
export function fieldInputClassNameLg(hasError) {
  return fieldInputClassName(hasError);
}

/**
 * Outer shell for icon + input (Login-style). Pair with `fieldInputInnerClassName()` on the `<input>`.
 */
export function fieldInputShellClassName(hasError) {
  const state = hasError
    ? `border-2 border-red-500 bg-red-50/50 ${SHELL_FOCUS_ERR}`
    : `border-2 border-slate-200 bg-white shadow-sm ${SHELL_FOCUS_OK}`;
  return `flex w-full min-h-11 items-center gap-3 ${BOX_ROUND} px-4 py-2.5 transition ${state}`;
}

/** Inner `<input>` when wrapped by `fieldInputShellClassName`. */
export function fieldInputInnerClassName() {
  return "min-w-0 flex-1 border-0 bg-transparent text-sm text-slate-700 shadow-none ring-0 outline-none placeholder:text-slate-400 focus:border-0 focus:outline-none focus:ring-0 focus-visible:outline-none";
}

/** DataTable / navbar search (left icon padding). */
export function fieldInputSearchClassName() {
  return `w-full min-h-11 min-w-0 ${BOX_ROUND} border-2 border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 ${FOCUS_OK}`;
}

/** Disabled / read-only display field */
export const fieldReadOnlyClassName =
  `w-full min-w-0 min-h-11 ${BOX_ROUND} border-2 border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-600 cursor-default outline-none`;
